import {
  Dispatch,
  FC,
  FormEventHandler,
  RefObject,
  SetStateAction,
  SyntheticEvent,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { getTransactionById, postImages } from 'services';
import { Transaction, convertSpecialCharacters, restoreSpecialCharacters } from 'types';

import { useTransactionApi } from 'hooks/use-transaction-api';
import { TireInspections } from 'pages/new-transaction/tires';
import { ImagesProvider } from 'pages/new-transaction/transaction-images';
import EditTransactionForm from './edit-transaction';

type EditTransactionContextType = {
  didSubmit: boolean;
  formRef: RefObject<HTMLFormElement>;
  transactionId: number;
  transaction?: Transaction | null;
  setTransaction: Dispatch<SetStateAction<Transaction | null | undefined>>;
  handleSubmit: React.FormEventHandler<HTMLFormElement>;
  images: string[];
  addImage: (src: string) => void;
  deleteImage: (src: string) => void;
};

const EditTransactionContext = createContext<EditTransactionContextType>({
  didSubmit: false,
  formRef: { current: null },
  transactionId: 0,
  setTransaction: () => {},
  handleSubmit: () => {},
  images: [],
  addImage: () => null,
  deleteImage: () => {}
});

export const useEditTransaction = () => useContext(EditTransactionContext);

const EditTransaction: FC = () => {
  const navigate = useNavigate();
  const params = useParams();

  const { editTransaction } = useTransactionApi();

  const transactionId = useMemo(() => Number(params.transactionId), [params.transactionId]);

  const formRef = useRef<HTMLFormElement>(null);

  const [didSubmit, setDidSubmit] = useState(false);
  const [transaction, setTransaction] = useState<Transaction | null>();
  const [images, setImages] = useState<string[]>(
    []
  );

  const addImage = useCallback(
    (imageBase64: string) => setImages(readOnly => [...readOnly, imageBase64 ]),
    [setImages]
  );
  const deleteImage = useCallback(
    (src: string) => setImages(readOnly => readOnly.filter(( imageBase64 ) => imageBase64 !== src)),
    [setImages]
  );

  const loadTransaction = useCallback(async () => {
    if (transactionId) {
      const t = await getTransactionById(transactionId);
      if (t?.damages) {
        t.damages = t.damages.map(d =>
          Object.fromEntries(Object.entries(d).map(([key, val]) => [key, restoreSpecialCharacters(val)]))
        );
      }
      if (t && !t?.tires?.length) {
        t.tires = Object.values(new TireInspections());
      }
      setTransaction(t);
    }
  }, [transactionId, setTransaction]);

  useEffect(() => {
    loadTransaction();
  }, [loadTransaction]);

  const handleSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    async (e: SyntheticEvent<HTMLFormElement, SubmitEvent>) => {
      console.log(images);
      e.preventDefault();
      if (!transaction) return;
      setDidSubmit(true);
      const damages = transaction.damages.map(d =>
        Object.fromEntries(Object.entries(d).map(([key, val]) => [key, convertSpecialCharacters(val)]))
      );
      for(const [idx, img] of images.entries()) {
        await postImages(transaction.id, { imageBase64: img, index: transaction.images.length + idx })
      }
      const response = await editTransaction({ ...transaction, damages });
      if (response) {
        navigate(`/transactions/${response.id}`);
      }
      setDidSubmit(false);
    },
    [transaction, editTransaction, navigate, images]
  );

  return (
    <ImagesProvider>
      <EditTransactionContext.Provider
        value={{ didSubmit, formRef, transactionId, transaction, setTransaction, handleSubmit, images, addImage, deleteImage }}
      >
        <EditTransactionForm />
      </EditTransactionContext.Provider>
    </ImagesProvider>
  );
};

export default EditTransaction;
